import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Droplets,
  Shield,
  FlaskConical,
  Wrench,
  GraduationCap,
  FileText,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Clock,
  Users,
  Building2,
  Phone,
  Mail,
  ArrowLeft,
  BookOpen,
  Award,
  AlertTriangle,
  Beaker,
  Settings,
  Target,
  Clipboard,
  Download,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

// Subcategory navigation items
const SUBCATEGORIES = [
  { id: "overview", label: "Overview", icon: Shield },
  { id: "water-testing", label: "Water Testing Services", icon: FlaskConical },
  { id: "consulting", label: "ST108 Consulting Services", icon: Clipboard },
  { id: "equipment", label: "ANSI/AAMI ST108 Equipment", icon: Wrench },
  { id: "training", label: "AAMI ST108 Tutorial Videos", icon: GraduationCap },
  { id: "resources", label: "ST108 Resources", icon: FileText }
];

// Overview Section Component
const OverviewSection = () => (
  <div className="space-y-8">
    {/* Hero Banner */}
    <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 rounded-2xl p-8 md:p-12 text-white">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
          <Shield className="w-6 h-6 text-cyan-300" />
        </div>
        <span className="text-cyan-300 font-semibold">AAMI ST108 Compliance</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Critical Water Systems for Healthcare
      </h1>
      <p className="text-blue-100 text-lg max-w-3xl leading-relaxed">
        ANSI/AAMI ST108 establishes comprehensive requirements for water quality in healthcare facilities. 
        Sterile Mate provides expert guidance, testing, and solutions to ensure your facility meets or exceeds 
        these critical standards for patient safety.
      </p>
    </div>

    {/* What is AAMI ST108 */}
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
        <BookOpen className="w-6 h-6 text-blue-600" />
        What is AAMI ST108?
      </h2>
      <p className="text-slate-600 mb-6 leading-relaxed">
        ANSI/AAMI ST108:2023 is the American National Standard that provides comprehensive guidance for water quality 
        in medical device processing. It covers all aspects of water used in healthcare facilities, including water 
        used for final rinsing of medical devices, steam generation for sterilization, and other critical applications.
      </p>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-blue-50 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Key Objectives
          </h3>
          <ul className="space-y-2 text-slate-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
              <span>Ensure patient safety through proper water quality standards</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
              <span>Prevent biofilm and microbial contamination</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
              <span>Establish monitoring and maintenance protocols</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
              <span>Provide guidance for equipment validation</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-amber-50 rounded-xl p-6">
          <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Who Must Comply?
          </h3>
          <ul className="space-y-2 text-slate-700">
            <li className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Hospitals and healthcare facilities</span>
            </li>
            <li className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Ambulatory surgery centers</span>
            </li>
            <li className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Dialysis centers</span>
            </li>
            <li className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Dental facilities</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    {/* Water Quality Levels */}
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
        <Droplets className="w-6 h-6 text-blue-600" />
        Water Quality Classifications
      </h2>
      
      <div className="grid md:grid-cols-3 gap-6">
        <div className="border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-b from-blue-50 to-white">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-white font-bold">1</span>
          </div>
          <h3 className="font-bold text-blue-900 text-lg mb-2">Utility Water</h3>
          <p className="text-slate-600 text-sm mb-4">Tap water meeting EPA standards. Used for initial cleaning and general facility use.</p>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>• Initial instrument cleaning</li>
            <li>• General facility cleaning</li>
            <li>• Non-critical applications</li>
          </ul>
        </div>
        
        <div className="border-2 border-cyan-200 rounded-xl p-6 bg-gradient-to-b from-cyan-50 to-white">
          <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-white font-bold">2</span>
          </div>
          <h3 className="font-bold text-cyan-900 text-lg mb-2">Critical Water</h3>
          <p className="text-slate-600 text-sm mb-4">Treated water for final rinsing of semi-critical and critical devices.</p>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>• Final rinse - semi-critical devices</li>
            <li>• Automated washer final cycles</li>
            <li>• Steam generation (some uses)</li>
          </ul>
        </div>
        
        <div className="border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-b from-purple-50 to-white">
          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-white font-bold">3</span>
          </div>
          <h3 className="font-bold text-purple-900 text-lg mb-2">Highly Purified Water</h3>
          <p className="text-slate-600 text-sm mb-4">Maximum purity for critical applications requiring sterility.</p>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>• Critical device final rinse</li>
            <li>• Sterile processing</li>
            <li>• Quality steam generation</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Why Choose Sterile Mate */}
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
      <h2 className="text-2xl font-bold mb-6">Why Choose Sterile Mate for ST108 Compliance?</h2>
      <div className="grid md:grid-cols-4 gap-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-600/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Award className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="font-semibold mb-2">Certified Experts</h3>
          <p className="text-slate-400 text-sm">Our team includes certified sterile processing professionals</p>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 bg-cyan-600/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Clock className="w-8 h-8 text-cyan-400" />
          </div>
          <h3 className="font-semibold mb-2">20+ Years Experience</h3>
          <p className="text-slate-400 text-sm">Decades of healthcare water treatment expertise</p>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-600/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="font-semibold mb-2">Comprehensive Solutions</h3>
          <p className="text-slate-400 text-sm">Testing, equipment, consulting, and training</p>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-600/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Users className="w-8 h-8 text-purple-400" />
          </div>
          <h3 className="font-semibold mb-2">Trusted Partner</h3>
          <p className="text-slate-400 text-sm">Serving healthcare facilities across Texas</p>
        </div>
      </div>
    </div>
  </div>
);

// Water Testing Services Section
const WaterTestingSection = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-cyan-900 via-blue-800 to-blue-900 rounded-2xl p-8 md:p-12 text-white">
      <div className="flex items-center gap-3 mb-4">
        <FlaskConical className="w-8 h-8 text-cyan-300" />
        <span className="text-cyan-300 font-semibold">Water Testing Services</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        ISO/IEC 17025:2017 Accredited Laboratory
      </h1>
      <p className="text-blue-100 text-lg max-w-3xl">
        Our accredited laboratory provides comprehensive water quality testing to ensure 
        your facility meets AAMI ST108 requirements and maintains patient safety.
      </p>
    </div>

    {/* Testing Services Grid */}
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-slate-800 mb-6">Available Testing Services</h2>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { name: "Heterotrophic Plate Count (HPC)", desc: "Bacterial enumeration", turnaround: "3-5 days" },
          { name: "Total Coliform / E. coli", desc: "Indicator organism testing", turnaround: "24-48 hours" },
          { name: "Endotoxin Testing", desc: "LAL/rFC methods", turnaround: "24-48 hours" },
          { name: "Legionella Species", desc: "Culture and PCR methods", turnaround: "10-14 days" },
          { name: "Pseudomonas aeruginosa", desc: "Specific pathogen detection", turnaround: "3-5 days" },
          { name: "Total Organic Carbon (TOC)", desc: "Organic contamination", turnaround: "1-2 days" },
          { name: "Conductivity/Resistivity", desc: "Ion content measurement", turnaround: "Same day" },
          { name: "pH Analysis", desc: "Acidity/alkalinity", turnaround: "Same day" },
          { name: "Hardness Testing", desc: "Calcium/magnesium levels", turnaround: "1-2 days" },
          { name: "Heavy Metals Panel", desc: "Lead, copper, zinc, etc.", turnaround: "5-7 days" },
          { name: "Chlorine/Chloramine", desc: "Disinfectant residuals", turnaround: "Same day" },
          { name: "Biofilm Assessment", desc: "Surface contamination", turnaround: "5-7 days" }
        ].map((test, idx) => (
          <div key={idx} className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all">
            <h3 className="font-semibold text-slate-800 mb-1">{test.name}</h3>
            <p className="text-sm text-slate-500 mb-2">{test.desc}</p>
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <Clock className="w-3 h-3" />
              <span>{test.turnaround}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Sample Collection */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Beaker className="w-5 h-5 text-blue-600" />
          Sample Collection Services
        </h3>
        <ul className="space-y-3 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>On-site sample collection by trained technicians</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Proper chain of custody documentation</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Temperature-controlled sample transport</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Sterile collection kits provided</span>
          </li>
        </ul>
      </div>
      
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Reporting & Documentation
        </h3>
        <ul className="space-y-3 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Electronic results delivery</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Compliance status reporting</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Trend analysis and recommendations</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Regulatory-ready documentation</span>
          </li>
        </ul>
      </div>
    </div>

    {/* CTA */}
    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <h3 className="font-bold text-blue-900 text-lg">Ready to schedule water testing?</h3>
        <p className="text-slate-600">Contact us for a custom testing program tailored to your facility.</p>
      </div>
      <Link to="/microbiology-testing">
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          View Full Testing Menu
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  </div>
);

// Consulting Services Section
const ConsultingSection = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-900 rounded-2xl p-8 md:p-12 text-white">
      <div className="flex items-center gap-3 mb-4">
        <Clipboard className="w-8 h-8 text-purple-300" />
        <span className="text-purple-300 font-semibold">Consulting Services</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        ST108 Consulting & Compliance Assessment
      </h1>
      <p className="text-purple-100 text-lg max-w-3xl">
        Our expert consultants help healthcare facilities navigate AAMI ST108 requirements,
        develop compliance programs, and implement best practices for water quality management.
      </p>
    </div>

    {/* Services */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Gap Analysis & Assessment</h3>
        <p className="text-slate-600 mb-4">
          Comprehensive evaluation of your current water quality program against AAMI ST108 requirements.
        </p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>On-site facility assessment</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Documentation review</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Compliance gap identification</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Prioritized action plan</span>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Policy & Procedure Development</h3>
        <p className="text-slate-600 mb-4">
          Custom policies and SOPs aligned with AAMI ST108 and your facility's specific needs.
        </p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Water quality management plan</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Monitoring protocols</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Corrective action procedures</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Documentation templates</span>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4">System Design & Validation</h3>
        <p className="text-slate-600 mb-4">
          Expert guidance on water treatment system design, installation, and validation.
        </p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>System specification development</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Vendor evaluation support</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>IQ/OQ/PQ validation protocols</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Commissioning support</span>
          </li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Ongoing Support</h3>
        <p className="text-slate-600 mb-4">
          Continuous support to maintain compliance and respond to changing requirements.
        </p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Regulatory update notifications</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Annual compliance reviews</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Survey preparation assistance</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" />
            <span>Technical phone support</span>
          </li>
        </ul>
      </div>
    </div>

    {/* CTA */}
    <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <h3 className="font-bold text-purple-900 text-lg">Schedule a Consultation</h3>
        <p className="text-slate-600">Let our experts assess your ST108 compliance needs.</p>
      </div>
      <Link to="/maintenance">
        <Button className="bg-purple-600 hover:bg-purple-700 text-white">
          Request Consultation
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  </div>
);

// Equipment Section
const EquipmentSection = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-orange-900 via-amber-800 to-yellow-900 rounded-2xl p-8 md:p-12 text-white">
      <div className="flex items-center gap-3 mb-4">
        <Wrench className="w-8 h-8 text-amber-300" />
        <span className="text-amber-300 font-semibold">Equipment Solutions</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        ANSI/AAMI ST108 Compliant Equipment
      </h1>
      <p className="text-orange-100 text-lg max-w-3xl">
        We provide, install, and service water purification systems designed specifically 
        to meet AAMI ST108 water quality requirements for healthcare facilities.
      </p>
    </div>

    {/* Equipment Categories */}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
          <Droplets className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Reverse Osmosis Systems</h3>
        <p className="text-slate-600 mb-4">
          Medical-grade RO systems for producing critical and highly purified water.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• Single and double-pass configurations</li>
          <li>• Automated monitoring and controls</li>
          <li>• Various capacity options</li>
          <li>• Compact footprint designs</li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
          <Settings className="w-6 h-6 text-cyan-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">DI Tank Systems</h3>
        <p className="text-slate-600 mb-4">
          Deionization systems for polishing RO water to highest purity levels.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• Mixed bed deionizers</li>
          <li>• Service deionization programs</li>
          <li>• Continuous quality monitoring</li>
          <li>• Tank exchange services</li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
          <FlaskConical className="w-6 h-6 text-purple-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Point-of-Use Filters</h3>
        <p className="text-slate-600 mb-4">
          Final barrier filtration for critical water applications.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• 0.2 micron sterilizing filters</li>
          <li>• Carbon filtration</li>
          <li>• Ultrafiltration membranes</li>
          <li>• Sub-micron particle removal</li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
          <Beaker className="w-6 h-6 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">UV Disinfection</h3>
        <p className="text-slate-600 mb-4">
          Ultraviolet systems for microbial control without chemical addition.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• 254nm germicidal UV</li>
          <li>• TOC reduction UV</li>
          <li>• Inline monitoring</li>
          <li>• Validated reduction logs</li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
          <Target className="w-6 h-6 text-amber-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Monitoring Systems</h3>
        <p className="text-slate-600 mb-4">
          Continuous water quality monitoring and data logging solutions.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• Conductivity monitors</li>
          <li>• TOC analyzers</li>
          <li>• pH monitoring</li>
          <li>• Remote alert systems</li>
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
          <Wrench className="w-6 h-6 text-red-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Service & Maintenance</h3>
        <p className="text-slate-600 mb-4">
          Preventive maintenance programs to ensure optimal system performance.
        </p>
        <ul className="text-sm text-slate-500 space-y-1">
          <li>• Scheduled PM visits</li>
          <li>• Filter replacements</li>
          <li>• Membrane cleaning</li>
          <li>• Emergency response</li>
        </ul>
      </div>
    </div>

    {/* CTA */}
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <h3 className="font-bold text-amber-900 text-lg">Need Equipment Service or Quote?</h3>
        <p className="text-slate-600">Contact us for equipment evaluation, quotes, or service requests.</p>
      </div>
      <Link to="/order-request">
        <Button className="bg-amber-600 hover:bg-amber-700 text-white">
          Request Quote
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  </div>
);

// Training Section - Coming Soon
const TrainingSection = () => {
  return (
    <div className="space-y-8">
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 rounded-2xl p-8 md:p-12 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <svg className="w-6 h-6 text-emerald-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
          <span className="text-emerald-300 font-semibold">Video Tutorials</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          AAMI ST108 Tutorial Videos
        </h1>
        <p className="text-green-100 text-lg max-w-3xl">
          Learn essential training tips and procedures for AAMI ST108 compliance through our 
          comprehensive video library. From basic water quality concepts to advanced troubleshooting.
        </p>
      </div>

      {/* Coming Soon Card */}
      <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-12 h-12 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-4 font-manrope">Video Tutorials Coming Soon</h2>
        <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">
          We're developing a comprehensive video training library covering all aspects of AAMI ST108 compliance. 
          Topics will include water quality testing, RO system maintenance, biofilm prevention, and documentation best practices.
        </p>
        
        {/* Preview Topics */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
          {[
            { title: "Water Quality Basics", desc: "Understanding water classifications" },
            { title: "Daily Monitoring", desc: "Testing procedures & documentation" },
            { title: "RO System Operations", desc: "Daily checks & maintenance" },
            { title: "Microbial Testing", desc: "Sample collection techniques" },
            { title: "Biofilm Prevention", desc: "Prevention strategies" },
            { title: "Compliance Documentation", desc: "Record keeping & audits" }
          ].map((topic, idx) => (
            <div key={idx} className="bg-slate-50 rounded-xl p-4 text-left">
              <h4 className="font-semibold text-slate-800 mb-1">{topic.title}</h4>
              <p className="text-sm text-slate-500">{topic.desc}</p>
            </div>
          ))}
        </div>

        {/* Notification Signup */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 max-w-md mx-auto">
          <h3 className="font-semibold text-emerald-900 mb-2">Get Notified When Available</h3>
          <p className="text-slate-600 text-sm mb-4">Contact us to be notified when our video library launches.</p>
          <Link to="/maintenance">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </Link>
        </div>
      </div>

      {/* Benefits */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-emerald-50 rounded-xl p-6">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 className="font-bold text-emerald-900 mb-2">Self-Paced Learning</h3>
          <p className="text-slate-600 text-sm">Watch videos anytime, anywhere. Pause, rewind, and review at your own pace.</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-bold text-blue-900 mb-2">Expert Instructors</h3>
          <p className="text-slate-600 text-sm">Training delivered by certified water quality professionals with real-world experience.</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-6">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
            <Award className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-bold text-purple-900 mb-2">Practical Skills</h3>
          <p className="text-slate-600 text-sm">Hands-on procedures you can immediately apply to your daily operations.</p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 text-white">
        <div>
          <h3 className="font-bold text-xl">Need Customized Training Now?</h3>
          <p className="text-emerald-100">Contact us for on-site training tailored to your facility's specific needs.</p>
        </div>
        <Link to="/maintenance">
          <Button className="bg-white text-emerald-700 hover:bg-emerald-50">
            Request Custom Training
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Resources Section
const ResourcesSection = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 rounded-2xl p-8 md:p-12 text-white">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-8 h-8 text-slate-300" />
        <span className="text-slate-300 font-semibold">Resources</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        ST108 Resources & Downloads
      </h1>
      <p className="text-slate-300 text-lg max-w-3xl">
        Access helpful resources, downloads, and reference materials related to 
        AAMI ST108 compliance and healthcare water quality management.
      </p>
    </div>

    {/* Featured Resource - Sterile Mate Guide */}
    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-700 rounded-2xl p-6 md:p-8 text-white shadow-xl">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-24 h-24 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg" 
              alt="Sterile Mate" 
              className="w-20 h-20 object-contain rounded-xl"
            />
          </div>
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm mb-3">
            <Award className="w-4 h-4" />
            <span>Featured Guide</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-2">
            Sterile Mate AAMI ST108 Compliance Guide
          </h3>
          <p className="text-blue-100 mb-4">
            Your comprehensive guide to understanding and implementing ANSI/AAMI ST108 water quality standards 
            for medical device reprocessing. Includes water quality specifications, implementation roadmaps, 
            and Sterile Mate's solutions.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link to="/st108/guide">
              <Button className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-6" data-testid="view-st108-guide-btn">
                <BookOpen className="w-4 h-4 mr-2" />
                View Interactive Guide
              </Button>
            </Link>
            <Link to="/st108/guide">
              <Button variant="outline" className="border-white/50 text-white hover:bg-white/10" data-testid="download-st108-guide-btn">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>

    {/* Resources Grid */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-600" />
          Official Standards & Documents
        </h3>
        <p className="text-slate-500 text-sm mb-4">
          Access official AAMI standards and regulatory guidance documents.
        </p>
        <div className="space-y-3">
          {[
            { name: "AAMI ST108:2023 Standard", type: "Official Document", url: "https://www.aami.org/standards/st108", desc: "Purchase official standard" },
            { name: "AAMI TIR34 Water Quality", type: "Technical Report", url: "https://www.aami.org/standards/tir34", desc: "Water quality guidance" },
            { name: "CMS Water Management", type: "CMS Guidelines", url: "https://www.cms.gov/medicare/health-safety-standards/quality-safety-oversight-general-information/infection-control-surveyor-tools", desc: "Federal requirements" },
            { name: "Joint Commission EC Standards", type: "Reference", url: "https://www.jointcommission.org/standards/", desc: "Environment of Care" },
            { name: "ASHRAE 188 Legionella", type: "Standard", url: "https://www.ashrae.org/technical-resources/bookstore/ansi-ashrae-standard-188-2021", desc: "Building water systems" }
          ].map((resource, idx) => (
            <a 
              key={idx} 
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors"
            >
              <div>
                <p className="font-medium text-slate-800">{resource.name}</p>
                <p className="text-xs text-slate-500">{resource.type} • {resource.desc}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-blue-600" />
            </a>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-blue-600" />
          External Resources
        </h3>
        <p className="text-slate-500 text-sm mb-4">
          Helpful links to authoritative organizations and guidance documents.
        </p>
        <div className="space-y-3">
          {[
            { name: "AAMI Official Website", desc: "Standards and publications", url: "https://www.aami.org/" },
            { name: "CMS Survey Guidelines", desc: "Federal compliance requirements", url: "https://www.cms.gov/Regulations-and-Guidance/Guidance/Manuals/Downloads/som107ap_a_hospitals.pdf" },
            { name: "Joint Commission Standards", desc: "Accreditation requirements", url: "https://www.jointcommission.org/" },
            { name: "APIC Water Management", desc: "Infection prevention resources", url: "https://apic.org/resources/topic-specific-infection-prevention/water-management/" },
            { name: "CDC Healthcare Water Safety", desc: "Public health guidance", url: "https://www.cdc.gov/healthcare-associated-infections/php/water-management/index.html" }
          ].map((resource, idx) => (
            <a 
              key={idx} 
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors"
            >
              <div>
                <p className="font-medium text-slate-800">{resource.name}</p>
                <p className="text-xs text-slate-500">{resource.desc}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-blue-600" />
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* FAQ Section */}
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-slate-800 mb-6">Frequently Asked Questions</h2>
      
      <div className="space-y-4">
        {[
          {
            q: "Is AAMI ST108 compliance mandatory?",
            a: "While AAMI ST108 is a voluntary standard, many accreditation bodies (TJC, AAAHC) and state regulators reference it, making compliance effectively required for most healthcare facilities."
          },
          {
            q: "How often should water quality testing be performed?",
            a: "AAMI ST108 recommends daily monitoring of basic parameters (conductivity, pH) and periodic microbial testing (typically weekly to monthly depending on the application and risk level)."
          },
          {
            q: "What happens if water quality fails to meet specifications?",
            a: "Facilities should have documented corrective action procedures. This typically includes retesting, system evaluation, maintenance, and potentially discontinuing use of affected water until specifications are met."
          },
          {
            q: "Does ST108 apply to dental practices?",
            a: "Yes, AAMI ST108 provides guidance for dental facilities, particularly regarding water used in dental units and instrument processing. Requirements are scaled to the clinical setting."
          }
        ].map((faq, idx) => (
          <div key={idx} className="border border-slate-200 rounded-xl p-5">
            <h3 className="font-semibold text-slate-800 mb-2">{faq.q}</h3>
            <p className="text-slate-600 text-sm">{faq.a}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Contact */}
    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-blue-900 text-lg">Have Questions?</h3>
            <p className="text-slate-600">Our ST108 experts are here to help</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <a href="tel:800-988-3892" className="text-blue-600 font-semibold text-lg hover:text-blue-700">
            800.988.3892
          </a>
          <Link to="/maintenance">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Contact Us
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Main Page Component
export default function AAMIST108Page() {
  const [activeSection, setActiveSection] = useState("overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewSection />;
      case "water-testing":
        return <WaterTestingSection />;
      case "consulting":
        return <ConsultingSection />;
      case "equipment":
        return <EquipmentSection />;
      case "training":
        return <TrainingSection />;
      case "resources":
        return <ResourcesSection />;
      default:
        return <OverviewSection />;
    }
  };

  const currentCategory = SUBCATEGORIES.find(c => c.id === activeSection);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <SiteHeader />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline font-medium">Back to Home</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <span className="font-bold text-slate-800">AAMI ST108 Compliance</span>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600 hover:text-blue-600"
              data-testid="st108-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex overflow-x-auto py-2 gap-1 scrollbar-hide">
            {SUBCATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeSection === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveSection(cat.id)}
                  data-testid={`st108-nav-${cat.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-600 hover:bg-slate-100 hover:text-blue-600"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-2 border-t border-slate-200">
              <div className="space-y-1">
                {SUBCATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeSection === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveSection(cat.id);
                        setMobileMenuOpen(false);
                      }}
                      data-testid={`st108-mobile-nav-${cat.id}`}
                      className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {renderSection()}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-400" />
              <div>
                <p className="font-bold">AskIsaac Inc.</p>
                <p className="text-sm text-slate-400">Your Healthcare Water Quality Partner</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="tel:800-988-3892" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                800.988.3892
              </a>
              <a href="mailto:support@sterilemate.com" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                support@sterilemate.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
